import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import useActiveSpecialistRole from "../../../app/hooks/useActiveSpecialistRole";
import { canSaveFlowRecord, resolveAuthUserId } from "../../../app/helpers/flowEditPageHelper";
import { readFichaReturnContext } from "../../../app/helpers/moduleFichaNavHelper";
import { useLocation } from "react-router-dom";
import { normalizeSpecialistRoleKey } from "../../../app/helpers/activeSpecialistRoleHelper";
import { resolveFlowRoleContext, resolveRecordExcelRoleKey } from "../../../app/helpers/flowEngineConfigHelper";
import { resolveCasePhase, resolveExcelRoleKeyForPhase } from "../../../app/helpers/flowCasePhaseHelper";
import RecordExcelUpdateModal from "./RecordExcelUpdateModal";

const RecordExcelUpdateRowButton = ({
  record,
  moduleProcess = "",
  onApplied,
  tooltipScope = "row",
  className = "",
  subjectKind = ""
}) => {
  const { t } = useTranslation();
  const configuration = useSelector((state) => state.configuration);
  const flowEngine = useSelector((state) => state.flowEngine);
  const userRole = useSelector((state) => state.userRole);
  const auth = useSelector((state) => state.auth);
  const location = useLocation();
  const { flowEngineEnabled, roleKey, availableRoles } = useActiveSpecialistRole();
  const extraRoleKeys = useMemo(
    () => availableRoles.map((role) => role.roleKey || normalizeSpecialistRoleKey(role)).filter(Boolean),
    [availableRoles]
  );
  const catalogRoles = useSelector((state) => state.catalogRole?.catalogRoles || []);
  const recordRoleName = catalogRoles.find(
    (role) => Number(role.id) === Number(record?.current_role_catalog_id)
  )?.name;
  const casePhase = resolveCasePhase(record, {
    roleName: recordRoleName,
    process: moduleProcess
  });
  const excelRoleKey = resolveExcelRoleKeyForPhase(
    casePhase,
    roleKey,
    resolveRecordExcelRoleKey(configuration, moduleProcess, roleKey, extraRoleKeys)
  );
  const showExcelButton = Boolean(moduleProcess && excelRoleKey && (record?.id || record?.name));
  const roleContext = resolveFlowRoleContext({ userRole, authUser: auth?.user });
  const saveRoleId = roleContext.isAdmin
    ? Number(record?.current_role_catalog_id || roleContext.roleId || 0)
    : roleContext.roleId;
  const excelDisabled = !canSaveFlowRecord({
    record,
    roleId: saveRoleId,
    flowEngineEnabled,
    flowEngine,
    roleKey,
    actorUserId: resolveAuthUserId(auth?.user),
    trayView: location.state?.trayView || readFichaReturnContext(moduleProcess).trayView || ""
  });
  const [excelOpen, setExcelOpen] = useState(false);
  const excelTipId = `excelTip-${tooltipScope}`;

  if (!showExcelButton) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        data-tip
        data-for={excelTipId}
        className={`table-button-excel ${className}`.trim()}
        aria-label={t("record-excel-update.title")}
        onClick={() => setExcelOpen(true)}
      >
        <i className="fa fa-file-excel" />
      </button>
      <ReactTooltip id={excelTipId} place="left" effect="solid">
        {t("record-excel-update.title")}
      </ReactTooltip>
      <RecordExcelUpdateModal
        isOpen={excelOpen}
        toggle={() => setExcelOpen(false)}
        moduleProcess={moduleProcess}
        expectedName={record.vision || record.name}
        expectedId={record.id}
        roleKey={excelRoleKey}
        casePhase={casePhase}
        subjectKind={subjectKind || record?.subject_kind || ""}
        canApply={!excelDisabled}
        onApplied={onApplied}
      />
    </>
  );
};

RecordExcelUpdateRowButton.propTypes = {
  record: PropTypes.object,
  moduleProcess: PropTypes.string,
  onApplied: PropTypes.func,
  tooltipScope: PropTypes.string,
  className: PropTypes.string,
  subjectKind: PropTypes.string
};

export default RecordExcelUpdateRowButton;
